<template>
  <div id="client-area-profile">
    <h2>{{ $t('user') }}</h2>
    <!-- Personal Info -->
    <div id="user-info">
      <!-- Avatar -->
      <div v-if="userProfile.avatar" class="editUser">
        <Avatar
          :key="avatarKey"
          :size="158"
          :src="userProfile.avatar"
        />
      </div>
      <div id="avatar-upload">
        <label for="file" class="import-file">
          <img :src="uploadIconSvg" alt="upload" />
          <div id="allowed-picture-extension">
            {{ allowedPictureExtension }}
          </div>
        </label>
        <input
          id="file"
          ref="avatarUploader"
          style="display: none"
          type="file"
          name="file"
          accept="image/png,image/jpg,image/jpeg"
          @change="onImageSelected"
        />
      </div>
      <form style="width: 100%" @submit.prevent="saveNewPersonalData">
        <DxForm
          id="user-personal-info"
          :form-data="userProfile"
          label-location="top"
        >
          <DxGroupItem :col-count="2">
            <DxItem
              css-class="w-full"
              :editor-options="{ disabled: !isAdmin }"
              data-field="name"
            >
              <DxLabel :text="$t('name')" />
            </DxItem>

            <DxItem
              :editor-options="{ disabled: !isAdmin }"
              data-field="surname"
            >
              <DxLabel :text="$t('surname')" />
            </DxItem>
          </DxGroupItem>

          <DxGroupItem :col-count="3">
            <DxItem
              :editor-options="{
                mask: '+34 000-000-000',
                disabled: !isAdmin,
                maskRules: { X: /[02-9]/ },
              }"
              data-field="phone"
            >
              <DxLabel :text="$t('phone')" />
            </DxItem>
            <DxItem
              data-field="email"
              :editor-options="{ disabled: true }"
            >
              <DxLabel :text="$t('email')" />
            </DxItem>
            <DxItem
              :editor-options="{
                value: languageValue,
                items: languages,
                valueExpr: 'value',
                disabled: false,
                displayExpr: translateLanguageOption,
                onValueChanged: languageChanged,
              }"
              editor-type="dxSelectBox"
            >
              <DxLabel :text="$t('language')" />
            </DxItem>
          </DxGroupItem>
        </DxForm>
        <DxForm
          id="user-passwords"
          :form-data="userPasswords"
          label-location="top"
          :col-count="3"
        >
          <DxSimpleItem
            data-field="oldPassword"
            editor-type="dxTextBox"
            :editor-options="passwordEditorOptions"
          >
            <DxLabel :text="$t('oldPassword')" />
          </DxSimpleItem>

          <DxSimpleItem
            data-field="newPassword"
            editor-type="dxTextBox"
            :editor-options="passwordEditorOptions"
          >
            <DxLabel :text="$t('new_password')" />
          </DxSimpleItem>

          <DxSimpleItem
            data-field="confirmNewPassword"
            editor-type="dxTextBox"
            :editor-options="passwordEditorOptions"
          >
            <DxLabel :text="$t('confirm_new_password')" />
            <DxCompareRule
              :comparison-target="passwordComparison"
              :message="$t('new_password_not_match')"
            />
          </DxSimpleItem>
        </DxForm>
      </form>
    </div>

    <h2>{{ $t('client') }}</h2>
    <form id="client-info" @submit.prevent="submitForm">
      <DxForm
        id="client-info-form"
        label-location="top"
        :form-data="contactInfo"
      >
        <DxGroupItem :col-count="3">
          <DxItem
            :editor-options="{ disabled: true }"
            data-field="country"
          >
            <DxLabel :text="$t('country')" />
          </DxItem>
          <DxItem
            :editor-options="{ disabled: true }"
            data-field="company"
          >
            <DxLabel :text="$t('clientCode')" />
          </DxItem>

          <DxItem
            :editor-options="{ disabled: true }"
            data-field="companyName"
          >
            <DxLabel :text="$t('clientCompanyName')" />
          </DxItem>
        </DxGroupItem>
        <DxGroupItem :col-count="4">
          <DxItem
            :editor-options="{ disabled: true }"
            data-field="language"
          >
            <DxLabel :text="$t('language')" />
          </DxItem>

          <DxItem
            :editor-options="{ disabled: true }"
            data-field="currency"
          >
            <DxLabel :text="$t('clientCurrency')" />
          </DxItem>

          <DxItem
            :editor-options="{ disabled: true }"
            data-field="identification_number"
          >
            <DxLabel :text="$t('NIF')" />
          </DxItem>

          <DxItem
            :editor-options="{ disabled: true }"
            data-field="agent"
          >
            <DxLabel :text="$t('clientAgent')" />
          </DxItem>
        </DxGroupItem>
      </DxForm>
    </form>

    <h2>{{ $t('address') }}</h2>
    <form id="address-info" @submit.prevent="submitForm">
      <DxForm label-location="top" :form-data="contactInfo">
        <DxGroupItem :col-count="3">
          <DxItem
            :editor-options="{ disabled: true }"
            data-field="addressName"
          >
            <DxLabel :text="$t('description')" />
          </DxItem>

          <DxItem
            :editor-options="{ disabled: true }"
            data-field="address"
          >
            <DxLabel :text="$t('address')" />
          </DxItem>

          <DxItem
            :editor-options="{ disabled: true }"
            data-field="city"
          >
            <DxLabel :text="$t('city')" />
          </DxItem>

          <DxItem
            class="comuna"
            :editor-options="{ disabled: true }"
            :data-field="dataField"
          >
            <DxLabel :text="labelText" />
          </DxItem>

          <DxItem
            :editor-options="{ disabled: true }"
            data-field="fax"
          >
            <DxLabel :text="$t('Fax')" />
          </DxItem>

          <DxItem
            :editor-options="{ disabled: true }"
            data-field="phone"
          >
            <DxLabel :text="$t('phone')" />
          </DxItem>
        </DxGroupItem>
      </DxForm>
    </form>

    <f7-button
      class="blue-button-save"
      style="max-width: 150px"
      fill
      type="default"
      styling-mode="contained"
      name="ButtonSave"
      @click="$emit('save-personal-data')"
    >
      {{ $t('save') }}
    </f7-button>
  </div>
</template>

<script>
import {
  DxForm,
  DxItem,
  DxSimpleItem,
  DxGroupItem,
  DxRequiredRule,
  DxCompareRule,
  DxLabel,
} from 'devextreme-vue/form';
import Avatar from 'vue-avatar';
import uploadIconSvg from '@/static/icons/upload-green.svg';
import {
  SECONDARY_COLOR,
  ALL_LANGUAGES,
  NOTIFY_TIME_LONG,
} from '@/js/constants';
import { mapActions, mapState, mapGetters } from 'vuex';
import ProfilePage from '@/pages/profile-page.vue';

export default {
  name: 'ClientAreaProfile',
  components: {
    DxForm,
    DxItem,
    DxSimpleItem,
    DxGroupItem,
    DxRequiredRule,
    DxCompareRule,
    DxLabel,
    Avatar,
  },
  data() {
    return {
      ProfilePage,
      contraseña: false,
      languages: ALL_LANGUAGES,
      // No enlazar directamente con i18next: al guardar, el formulario puede
      // reconstruirse antes de que termine la petición y emitir otro valor.
      languageValue:
        this.$root?.$i18n?.i18next?.language ||
        ALL_LANGUAGES[0]?.value,
      languageSelected: '',
      loaded: false,
      themes: ['Light'],
      isUserChangingPassword: false,
      passwordEditorOptions: {
        mode: 'password',
        showClearButton: false,
        buttons: [],
        valueChangeEvent: 'input',
      },
      SECONDARY_COLOR,
      selectedImage: this.avatar,
      selectedImageName: '',
      isSaveConfigDisabled: true,
      savePersonalButtonOptions: {
        text: `${this.$t('save_changes')}`,
        type: 'success',
        useSubmitBehavior: true,
      },
      submitChangePasswordOptions: {
        text: `${this.$t('save_new_password')}`,
        type: 'success',
        useSubmitBehavior: true,
      },
      submitConfigOptions: {
        text: `${this.$t('save_configuration')}`,
        type: 'success',
        useSubmitBehavior: true,
        disabled: true,
      },
      cancelChangePasswordOptions: {
        text: `${this.$t('cancel')}`,
        type: 'danger',
        onClick: this.hideChangePassword,
      },
      allowedPictureExtension: '.png, .jpg, .jpeg',
      avatarKey: 'avatar-key',
    };
  },
  computed: {
    uploadIconSvg: () => uploadIconSvg,
    ...mapGetters('user', ['isAdmin', 'userProfile']),
    ...mapGetters('company', ['userInfo']),
    ...mapState('user', ['userPasswords']),
    ...mapState('contact', [
      'showNewContactPopup',
      'enabled',
      'company',
      'companyName',
      'address',
      'addressName',
    ]),
    ...mapGetters('contact', ['contactInfo']),

    labelText() {
      return this.contactInfo.country === 'CL'
        ? this.$t('district')
        : this.$t('province');
    },
    dataField() {
      return this.contactInfo.country === 'CL'
        ? 'desc_comuna'
        : 'province';
    },
  },
  async beforeMount() {
    this.$f7.preloader.show();
    this.loaded = true;
    this.$f7.preloader.hide();
  },
  methods: {
    ...mapActions('user', [
      'updatePassword',
      'updateAvatar',
      'updateUserPersonalData',
      'setUserNewPassword',
      'setUserOldPassword',
      'setConfirmNewPassword',
    ]),

    showChangePassword() {
      this.isUserChangingPassword = true;
    },
    goToCreateUserPage() {
      this.$f7router.navigate('/users/create', {
        transition: 'f7-fade',
      });
    },
    switchIsChangingPassword() {
      this.isUserChangingPassword = !this.isUserChangingPassword;
    },
    hideChangePassword() {
      this.isUserChangingPassword = false;
    },
    initAvatarForm() {
      this.$refs.avatarUploader.value = null;
    },
    async saveNewPassword() {
      try {
        const oldPassword = (
          this.userPasswords.oldPassword || ''
        ).trim();
        const newPassword = (
          this.userPasswords.newPassword || ''
        ).trim();
        const confirmNewPassword = (
          this.userPasswords.confirmNewPassword || ''
        ).trim();

        // Exigir los 3
        if (!oldPassword || !newPassword || !confirmNewPassword) {
          this.$notifyDX(
            {
              message: this.$t('password_all_fields_required'),
              width: 450,
            },
            'error',
            NOTIFY_TIME_LONG
          );
          return;
        }

        // Deben coincidir
        if (newPassword !== confirmNewPassword) {
          this.$notifyDX(
            {
              message: this.$t('new_password_not_match'),
              width: 450,
            },
            'error',
            NOTIFY_TIME_LONG
          );
          return;
        }

        // Evitar misma contraseña
        if (oldPassword === newPassword) {
          this.$notifyDX(
            {
              message: this.$t('new_password_must_be_different'),
              width: 450,
            },
            'error',
            NOTIFY_TIME_LONG
          );
          return;
        }

        this.$notifyDX(
          { message: this.$t('updating_new_password'), width: 450 },
          'info',
          2000
        );

        await this.updatePassword({ newPassword, oldPassword });

        this.$notifyDX(
          { message: this.$t('password_updated'), width: 450 },
          'success',
          5000
        );

        setTimeout(() => this.$f7.dialog.close(), 5000);
      } catch (error) {
        this.$f7.dialog.close();
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                JSON.parse(error.message).error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.initUserPasswordsForm();
      }
    },

    initUserPasswordsForm() {
      this.setUserOldPassword('');
      this.setUserNewPassword('');
      this.setConfirmNewPassword('');
    },

    onImageSelected(event) {
      const reader = new FileReader();
      const newImage = event.target.files[0];
      this.selectedImageName = newImage.name.replace(
        /(.{13})..+/,
        '$1...'
      );

      reader.onload = (e) => {
        this.selectedImage = e.target.result;
        this.userProfile.avatar = this.selectedImage;
        this.avatarKey = `avatar-key-${Math.random()}`;
        this.$emit('avatar-changed', this.selectedImage);
      };

      reader.onerror = (error) => {
        this.$f7.dialog.alert(error);
      };

      reader.readAsDataURL(newImage);
    },
    async onUpdatePhoto() {
      const self = this;
      try {
        if (!this.selectedImage) {
          this.$notifyDX(
            {
              message: this.$t('profilePhotoNotSelected'),
              width: 150,
            },
            'error',
            NOTIFY_TIME_LONG
          );
          return;
        }

        this.$notifyDX(
          {
            message: this.$t('updating_avatar'),
            width: 150,
          },
          'info',
          2000
        );

        await this.updateAvatar({ newAvatar: this.selectedImage });
        self.$f7.dialog.close();
        this.$notifyDX(
          {
            message: this.$t('avatar_updated'),
            width: 150,
          },
          'success',
          2000
        );
        this.selectedImage = undefined;
      } catch (error) {
        this.$f7.dialog.close();

        self.$f7.dialog.close();
        this.$notifyDX(
          {
            message: this.$t(error.errorCodes[0]),
            width: 150,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.initAvatarForm();
      }
    },
    getPhotoExtension(photoB64) {
      return photoB64.split(';base64,')[0].split('image/')[1];
    },
    languageChanged(e) {
      if (!e?.value) return;

      // Mantener estable el selector mientras el idioma global cambia y el
      // formulario se vuelve a renderizar tras guardar.
      this.languageValue = e.value;

      let key = '';
      for (const lang in ALL_LANGUAGES) {
        if (ALL_LANGUAGES[lang].value === e.value) {
          key = ALL_LANGUAGES[lang].key;
        }
      }
      this.$emit('languageChanged', { key, value: e.value });
    },
    passwordComparison() {
      return this.userPasswords.newPassword;
    },
    translateLanguageOption(e) {
      const key =
        (e && typeof e === 'object' ? e.value : e) || '';
      return key ? String(this.$t(key)) : '';
    },
    translateLang(row) {
      return this.$t(`${row.language}`);
    },
    ...mapActions('breadcrumb', ['setCurrentRoute']),
  },
};
</script>

<style lang="scss">
@import './Profile.styles.scss';
</style>
